import {HexCorner, HexEdge, HexFace} from '@colonist/game-mechanic-validators'
import {ArrayUtils} from '@colonist/utils'
import {TileHexState, TileEdgeState, TileCornerState, TileHexType, TileEdgeType, TileCornerBuildingType, TileCornerBonusType, PlayerColor} from '@colonist/game-states'

export function getAmountToGiveForCornerPiece(piece: TileCornerBuildingType): number {
    switch(piece) {
        case TileCornerBuildingType.KnightActiveLevel1:
        case TileCornerBuildingType.KnightActiveLevel2:
        case TileCornerBuildingType.KnightActiveLevel3:
        case TileCornerBuildingType.KnightInactiveLevel1:
        case TileCornerBuildingType.KnightInactiveLevel2:
        case TileCornerBuildingType.KnightInactiveLevel3: return 0
        case TileCornerBuildingType.DestroyedCity:
        case TileCornerBuildingType.Settlement: return 1
        case TileCornerBuildingType.City: return 2
    }
}

export function isCornerBuildingKnight(cornerBuilding: TileCornerBuildingType | undefined): boolean {
    switch(cornerBuilding) {
        case TileCornerBuildingType.KnightActiveLevel1:
        case TileCornerBuildingType.KnightActiveLevel2:
        case TileCornerBuildingType.KnightActiveLevel3:
        case TileCornerBuildingType.KnightInactiveLevel1:
        case TileCornerBuildingType.KnightInactiveLevel2:
        case TileCornerBuildingType.KnightInactiveLevel3: return true
        default:
            return false
    }
}

export class TileCorner {
    readonly state: TileCornerState
    readonly hexCorner: HexCorner

    constructor(state: TileCornerState) {
        this.state = state
        this.hexCorner = new HexCorner(this.state.x, this.state.y, this.state.z)
    }

    get owner(): Exclude<PlayerColor, PlayerColor.None> | undefined {
        return this.state.owner
    }

    get buildingType(): TileCornerBuildingType | undefined {
        return this.state.buildingType
    }

    get restrictedStartingPlacement(): boolean {
        if(this.state.restrictedStartingPlacement == undefined) return false
        return this.state.restrictedStartingPlacement
    }

    get bonuses(): TileCornerBonusType[] {
        if(this.state.bonuses == undefined) return this.state.bonuses = []
        return this.state.bonuses
    }

    sameCornerLocation(otherCorner: TileCorner): boolean {
        if(this.hexCorner.x != otherCorner.hexCorner.x) return false
        if(this.hexCorner.y != otherCorner.hexCorner.y) return false
        if(this.hexCorner.z != otherCorner.hexCorner.z) return false // noinspection RedundantIfStatementJS
        return true
    }

    sameCorner(otherCorner: TileCorner): boolean {
        if(this.owner != otherCorner.owner) return false
        if(this.buildingType != otherCorner.buildingType) return false
        return this.sameCornerLocation(otherCorner)
    }

    hasCornerBonusOfType(cornerBonusType: TileCornerBonusType): boolean {
        for(const bonus of this.bonuses) {
            if(bonus == cornerBonusType) return true
        }
        return false
    }

    doesCornerHaveMetropolis(): boolean {
        if(ArrayUtils.elementInArray(this.bonuses, TileCornerBonusType.MetropolisPolitics)) return true
        if(ArrayUtils.elementInArray(this.bonuses, TileCornerBonusType.MetropolisScience)) return true
        if(ArrayUtils.elementInArray(this.bonuses, TileCornerBonusType.MetropolisTrade)) return true

        return false
    }

    getMetropolis(): TileCornerBonusType | undefined {
        if(ArrayUtils.elementInArray(this.bonuses, TileCornerBonusType.MetropolisPolitics)) return TileCornerBonusType.MetropolisPolitics
        if(ArrayUtils.elementInArray(this.bonuses, TileCornerBonusType.MetropolisScience)) return TileCornerBonusType.MetropolisScience
        if(ArrayUtils.elementInArray(this.bonuses, TileCornerBonusType.MetropolisTrade)) return TileCornerBonusType.MetropolisTrade

        return undefined
    }

    removeCornerBonus(cornerBonus: TileCornerBonusType) {
        ArrayUtils.removeElementFromArray(this.bonuses, cornerBonus)
    }
}

export class TileEdge {
    readonly state: TileEdgeState
    readonly hexEdge: HexEdge

    constructor(state: TileEdgeState) {
        this.state = state
        this.hexEdge = new HexEdge(this.state.x, this.state.y, this.state.z)
    }

    get owner(): Exclude<PlayerColor, PlayerColor.None> | undefined {
        return this.state.owner
    }

    get type(): TileEdgeType | undefined {
        return this.state.type
    }

    sameEdge(otherEdge: TileEdge): boolean {
        if(this.owner != otherEdge.owner) return false
        if(this.type != otherEdge.type) return false
        if(this.hexEdge.x != otherEdge.hexEdge.x) return false
        if(this.hexEdge.y != otherEdge.hexEdge.y) return false
        if(this.hexEdge.z != otherEdge.hexEdge.z) return false // noinspection RedundantIfStatementJS
        return true
    }
}

export class TileHex {
    readonly state: TileHexState
    readonly hexFace: HexFace

    constructor(state: TileHexState) {
        this.state = state
        this.hexFace = new HexFace(this.state.x, this.state.y)
    }

    get tileType(): TileHexType {
        return this.state.type
    }

    get diceNumber(): number {
        return this.state.diceNumber
    }

    get diceProbability(): number {
        switch(this.diceNumber) {
            case 2: return 1
            case 3: return 2
            case 4: return 3
            case 5: return 4
            case 6: return 5
            case 8: return 5
            case 9: return 4
            case 10: return 3
            case 11: return 2
            case 12: return 1
        }
        return 0
    }

    sameTileHex(tileHex: TileHex) {
        if(tileHex.hexFace.x != this.hexFace.x) return false
        if(tileHex.hexFace.y != this.hexFace.y) return false // noinspection RedundantIfStatementJS
        return true
    }

    isTileHexNeighbor(tileHex: TileHex): boolean {
        const neighboringHexFaces = tileHex.hexFace.neighbors()
        for(const neighbor of neighboringHexFaces) {
            if(neighbor.x != this.hexFace.x) continue
            if(neighbor.y != this.hexFace.y) continue
            return true
        }
        return false
    }

    isTileLand(): boolean {
        if(this.tileType == TileHexType.Sea) return false
        if(this.tileType == TileHexType.Fog) return false

        return true
    }
}
